import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Image } from 'expo-image';
import { useTheme, TYPOGRAPHY, SPACING, BORDER_RADIUS } from '../../theme';
import { formatShortDate } from '../../utils/time';
import { resolveAfterImage, type ResolvedPair } from '../../utils/beforeAfter';
import type { Post } from '../../types';

/** "Filed by Luis R., 12 June 2026", or Fire Scout when the post is anonymous. */
export function byline(post: Post): string {
  const who = post.isAnonymous ? 'Fire Scout' : post.authorName;
  const when = formatShortDate(post.createdAt);
  return when ? `Filed by ${who}, ${when}` : `Filed by ${who}`;
}

/**
 * A before/after pair, side by side and dated.
 *
 * The sample report put its before photos on page 9 and the matching after
 * photos on page 11, uncaptioned, which makes the comparison impossible and
 * wastes the only evidence that actually persuades anyone. Side by side is
 * the whole point.
 */
export function BeforeAfterFigure({ pair, testID }: { pair: ResolvedPair; testID?: string }) {
  const { colors } = useTheme();
  const before = pair.post.beforePostCreatedAt ?? undefined;

  return (
    <View testID={testID} style={styles.figure}>
      <View style={styles.pairRow}>
        <View style={styles.half}>
          <Image
            source={pair.beforeUri}
            placeholder={pair.beforePlaceholder}
            style={[styles.image, { backgroundColor: colors.surface }]}
            contentFit="cover"
          />
          <Text style={[styles.tag, { color: colors.textSecondary }]}>
            {before ? `Before · ${formatShortDate(before)}` : 'Before'}
          </Text>
        </View>
        <View style={styles.half}>
          <Image
            source={pair.afterUri}
            placeholder={pair.afterPlaceholder}
            style={[styles.image, { backgroundColor: colors.surface }]}
            contentFit="cover"
          />
          <Text style={[styles.tag, { color: colors.textSecondary }]}>
            {`After · ${formatShortDate(pair.post.createdAt) ?? ''}`.trim()}
          </Text>
        </View>
      </View>
      {pair.post.caption ? (
        <Text style={[styles.caption, { color: colors.text }]}>{pair.post.caption}</Text>
      ) : null}
      <Text style={[styles.byline, { color: colors.textTertiary }]}>{byline(pair.post)}</Text>
    </View>
  );
}

/** A single observation photo, for work with no paired before shot. */
export function SinglePhotoFigure({ post }: { post: Post }) {
  const { colors } = useTheme();
  const uri = resolveAfterImage(post, true);
  if (!uri) return null;

  return (
    <View style={styles.figure}>
      <Image
        source={uri}
        placeholder={post.thumbnailUrl}
        style={[styles.single, { backgroundColor: colors.surface }]}
        contentFit="cover"
      />
      {post.caption ? (
        <Text style={[styles.caption, { color: colors.text }]}>{post.caption}</Text>
      ) : null}
      <Text style={[styles.byline, { color: colors.textTertiary }]}>{byline(post)}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  figure: { gap: 4, marginTop: SPACING.sm },
  pairRow: { flexDirection: 'row', gap: SPACING.sm },
  half: { flex: 1, gap: 2 },
  image: { width: '100%', aspectRatio: 4 / 3, borderRadius: BORDER_RADIUS.sm },
  single: { width: 260, maxWidth: '100%', aspectRatio: 4 / 3, borderRadius: BORDER_RADIUS.sm },
  tag: { ...TYPOGRAPHY.caption },
  caption: { ...TYPOGRAPHY.bodySmall },
  byline: { ...TYPOGRAPHY.caption },
});
